import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';

import useNotification from '../../../../core/hooks/use_notification';
import { Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { getWarehouseDocumentsState } from '../../../../features/inventory/get_warehouse_documents';
import { generateWarehouseDocumentLabelsAction } from '../../../../features/inventory/generate_warehouse_document_labels';
import { generateWarehouseDocumentLabelsNotificationData } from '../../../../core/utils/notification_utils';
import { GET_ROUTE_WAREHOUSE_DOCUMENT, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../core/constants';
import { CustomIconButton } from '../../../common/button';

const WarehouseDocumentsList = ({ queryParams }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const getWarehouseDocuments = useSelector(getWarehouseDocumentsState);

    const [loadingLabelIds, setLoadingLabelIds] = useState([]);

    const onGenerateWarehouseDocumentLabels = (warehouseDocumentId) => {
        setLoadingLabelIds(prev => [...prev, warehouseDocumentId]);

        dispatch(generateWarehouseDocumentLabelsAction({ id: warehouseDocumentId }))
            .then(response => {
                showNotification(generateWarehouseDocumentLabelsNotificationData(response));
            })
            .finally(() => {
                setLoadingLabelIds(prev => prev.filter(id => id !== warehouseDocumentId));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell align="center">Typ</TableCell>
                        <TableCell align="center">Zatwierdzenie</TableCell>
                        <TableCell>Numer</TableCell>
                        <TableCell align="center">Data utworzenia</TableCell>
                        <TableCell align="center">Data dostawy</TableCell>
                        <TableCell>Kontrahent</TableCell>
                        <TableCell align="right" sx={ { minWidth: 140 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { getWarehouseDocuments?.data?.content?.map(warehouseDocument => (
                        <TableRow key={ warehouseDocument?.id } hover>
                            <TableCell align="center">
                                <Typography variant="body2" sx={ { fontWeight: 600, color: 'text.secondary' } }>
                                    { warehouseDocument?.documentType }
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={ warehouseDocument?.confirmed ? "Zatwierdzony" : "Oczekuje" }
                                    color={ warehouseDocument?.confirmed ? "success" : "warning" }
                                    size="small"
                                />
                            </TableCell>
                            <TableCell sx={ { fontWeight: 500 } }>
                                <Box display="flex" alignItems="center" flexWrap="wrap" gap={ 1 }>
                                    { warehouseDocument?.name }
                                    { warehouseDocument?.purposeType === 'RESERVATION' && (
                                        <Chip
                                            title="Rezerwacje użytkowników"
                                            label="R"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                        />
                                    ) }
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                { moment(warehouseDocument?.issueDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                            </TableCell>
                            <TableCell align="center">
                                { warehouseDocument?.deliveryDate ? moment(warehouseDocument?.deliveryDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) : '-' }
                            </TableCell>
                            <TableCell>
                                { warehouseDocument?.counterpartyName }
                            </TableCell>
                            <TableCell align="right">
                                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={ 1 }>
                                    <CustomIconButton
                                        title="Pobierz naklejki"
                                        isLoading={ loadingLabelIds.includes(warehouseDocument?.id) }
                                        onClick={ () => onGenerateWarehouseDocumentLabels(warehouseDocument?.id) }
                                        icon={ <DownloadIcon fontSize="small"/> }
                                    />

                                    { (warehouseDocument?.documentType === 'MW' && !warehouseDocument?.confirmed) ? (
                                        <Tooltip title="Edytuj">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={ () => navigate(GET_ROUTE_WAREHOUSE_DOCUMENT(warehouseDocument?.id), {
                                                    state: {
                                                        queryParams
                                                    }
                                                }) }
                                            >
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Podgląd">
                                            <IconButton
                                                size="small"
                                                onClick={ () => navigate(GET_ROUTE_WAREHOUSE_DOCUMENT(warehouseDocument?.id), {
                                                    state: {
                                                        queryParams
                                                    }
                                                }) }
                                            >
                                                <SearchIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    ) }
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )) }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default WarehouseDocumentsList;